/**
 * Shared time helpers for the §6.E "Schedule a game" UI — so the scheduling
 * modal and the invited-friend card render a game's time identically. Pure +
 * dependency-free (no date lib); all formatting via {@link Intl} on the
 * browser's local zone.
 */

/** A friendly absolute time, e.g. "Fri, Jun 27 · 8:00 PM". */
export function formatGameTime(epochMs: number): string {
  try {
    const d = new Date(epochMs);
    const date = d.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
    const time = d.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
    return `${date} · ${time}`;
  } catch {
    return new Date(epochMs).toString();
  }
}

/** A short relative countdown, e.g. "in 2h 14m", "in 3 days", "starting now". */
export function formatCountdown(epochMs: number, now: number = Date.now()): string {
  const ms = epochMs - now;
  if (ms <= 0) return 'starting now';
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'in under a minute';
  if (mins < 60) return `in ${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) {
    const remMins = mins % 60;
    return remMins > 0 ? `in ${hours}h ${remMins}m` : `in ${hours}h`;
  }
  const days = Math.floor(hours / 24);
  return days === 1 ? 'in 1 day' : `in ${days} days`;
}

/**
 * Format an epoch (ms) as the `YYYY-MM-DDTHH:mm` string a native
 * {@code <input type="datetime-local">} expects — in LOCAL time, since the
 * control is zone-naive and shows whatever string it's given. The inverse of
 * {@link localInputToEpoch}.
 */
export function epochToLocalInput(epochMs: number): string {
  const d = new Date(epochMs);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/** Parse a {@code <input type="datetime-local">} value (local, zone-naive)
 *  back to epoch ms. Returns NaN for an empty/invalid string. */
export function localInputToEpoch(value: string): number {
  if (!value) return NaN;
  return new Date(value).getTime();
}

/**
 * A sensible default start time for a fresh schedule: the upcoming Friday at
 * 8:00 PM local. If it's already Friday-evening-or-later, jump to NEXT Friday
 * so the default is always in the future. Always at least an hour out.
 */
export function defaultScheduleEpoch(now: number = Date.now()): number {
  const d = new Date(now);
  const FRIDAY = 5;
  let add = (FRIDAY - d.getDay() + 7) % 7;
  d.setHours(20, 0, 0, 0);
  // If that lands in the past (today is Friday but past 8pm, or earlier today
  // and add===0), push a week.
  if (add === 0 && d.getTime() <= now + 60 * 60 * 1000) add = 7;
  d.setDate(d.getDate() + add);
  // Floor guard — never return something within the next hour.
  if (d.getTime() < now + 60 * 60 * 1000) return now + 60 * 60 * 1000;
  return d.getTime();
}

/**
 * Build a Google Calendar "add event" URL for a scheduled game. A convenient
 * one-tap alternative to the .ics download for Gmail users. Default duration
 * 1 hour. Times are UTC in the basic-format Google expects.
 */
export function googleCalendarUrl(opts: {
  title: string;
  startMs: number;
  durationMin?: number;
  details?: string;
  location?: string;
}): string {
  const { title, startMs, durationMin = 60, details, location } = opts;
  const fmt = (ms: number) =>
    new Date(ms).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const dates = `${fmt(startMs)}/${fmt(startMs + durationMin * 60_000)}`;
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates,
  });
  if (details) params.set('details', details);
  if (location) params.set('location', location);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/** RFC5322-lite email validation — good enough to reject obvious typos in the
 *  chip input without rejecting legitimate addresses. */
export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}
